import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const API_BASE = 'https://system.sales-management.cloud/api/educational_materials.php';

const MATERIAL_TYPES = {
    text: { label: 'テキスト', icon: '📄', color: 'bg-blue-100' },
    image: { label: '画像', icon: '🖼️', color: 'bg-green-100' },
    video: { label: '動画', icon: '🎥', color: 'bg-red-100' },
    pdf: { label: 'PDF', icon: '📑', color: 'bg-yellow-100' }
};

function MaterialViewer() {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedMaterial, setExpandedMaterial] = useState(null);
    const [learningProgress, setLearningProgress] = useState({});
    const pageSize = 20;

    const normalizeMaterial = (material) => ({
        ...material,
        file_size: Number(material.file_size || 0),
        is_published: Number(material.is_published) === 1
    });

    useEffect(() => {
        fetchMaterials(1);
        loadProgress();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMaterials = async (page = 1) => {
        try {
            setLoading(true);
            setError('');
            const token = Cookies.get('auth_token');

            if (!token) {
                setError('認証情報が見つかりません');
                return;
            }

            const params = new URLSearchParams({
                action: 'list',
                limit: pageSize,
                offset: (page - 1) * pageSize,
                published_only: '1'
            });

            if (search) params.append('search', search);
            if (filterType) params.append('category', filterType);

            const response = await fetch(`${API_BASE}?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(data?.message || `API Error: ${response.status}`);
            }

            if (data?.status === 'success') {
                const normalizedMaterials = (data.data.materials || []).map(normalizeMaterial);
                setMaterials(normalizedMaterials);
                setTotalCount(Number(data.data.total || 0));
                setCurrentPage(page);
            } else {
                setError(data?.message || '教材の取得に失敗しました');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            setError(`エラー: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadProgress = () => {
        // ローカルストレージから進捗を読み込み
        const progress = localStorage.getItem('learningProgress');
        if (progress) {
            setLearningProgress(JSON.parse(progress));
        }
    };

    const recordProgress = (materialId) => {
        const updatedProgress = {
            ...learningProgress,
            [materialId]: {
                completed: true,
                completedDate: new Date().toISOString()
            }
        };
        setLearningProgress(updatedProgress);
        localStorage.setItem('learningProgress', JSON.stringify(updatedProgress));
    };

    const handleSearch = () => {
        fetchMaterials(1);
    };

    const toggleExpand = (materialId) => {
        setExpandedMaterial(expandedMaterial === materialId ? null : materialId);
    };

    const renderMaterialContent = (material) => {
        switch (material.material_type) {
            case 'text':
                return (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <p className="text-gray-700 whitespace-pre-wrap">{material.description}</p>
                    </div>
                );
            case 'image':
                return (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <img
                            src={material.file_path}
                            alt={material.title}
                            className="max-w-full h-auto rounded-lg"
                        />
                    </div>
                );
            case 'video':
                return (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <video
                            controls
                            className="w-full rounded-lg"
                            style={{ maxHeight: '500px' }}
                        >
                            <source src={material.file_path} type="video/mp4" />
                            ブラウザが動画再生に対応していません
                        </video>
                    </div>
                );
            case 'pdf':
                return (
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <iframe
                            src={material.file_path}
                            className="w-full rounded-lg"
                            style={{ height: '600px' }}
                            title={material.title}
                        ></iframe>
                    </div>
                );
            default:
                return null;
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);
    const isCompleted = (materialId) => learningProgress[materialId]?.completed || false;

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                {/* ヘッダー */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">学習教材</h1>
                    <p className="text-gray-600">公開されている教材を閲覧できます</p>
                </div>

                {/* エラー */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}

                {/* 検索・フィルター */}
                <div className="mb-6 bg-white rounded-lg shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">キーワード検索</label>
                            <div className="flex">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="タイトル、説明で検索"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <button
                                    onClick={handleSearch}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600"
                                >
                                    <SearchIcon sx={{ fontSize: 20 }} />
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">教材タイプ</label>
                            <select
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">すべて</option>
                                {Object.entries(MATERIAL_TYPES).map(([key, config]) => (
                                    <option key={key} value={key}>{config.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="text-sm text-gray-600">
                            全 {totalCount} 件の教材
                        </div>
                    </div>
                </div>

                {/* 教材リスト */}
                <div className="space-y-4">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500 bg-white rounded-lg">
                            読み込み中...
                        </div>
                    ) : materials.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 bg-white rounded-lg">
                            教材がありません
                        </div>
                    ) : (
                        materials.map(material => (
                            <div key={material.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                                <div
                                    className="p-4 cursor-pointer flex items-center justify-between gap-4"
                                    onClick={() => toggleExpand(material.id)}
                                >
                                    <div className="flex-1 flex items-start gap-4">
                                        <div className={`text-3xl ${MATERIAL_TYPES[material.material_type]?.color || 'bg-gray-100'} rounded-lg p-2 w-12 h-12 flex items-center justify-center`}>
                                            {MATERIAL_TYPES[material.material_type]?.icon || '📄'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-lg font-semibold text-gray-900">{material.title}</h3>
                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                    {MATERIAL_TYPES[material.material_type]?.label}
                                                </span>
                                                {isCompleted(material.id) && (
                                                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                                        ✓ 完了
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                                {material.description || '説明はありません'}
                                            </p>
                                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                <span>{(material.file_size / (1024 * 1024)).toFixed(2)} MB</span>
                                                <span>{new Date(material.created_at).toLocaleDateString('ja-JP')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {material.file_path && (
                                            <a
                                                href={material.file_path}
                                                download
                                                onClick={(e) => e.stopPropagation()}
                                                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                title="ダウンロード"
                                            >
                                                <GetAppIcon sx={{ fontSize: 20 }} />
                                            </a>
                                        )}
                                        <button
                                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                                        >
                                            <ExpandMoreIcon
                                                sx={{
                                                    fontSize: 24,
                                                    transform: expandedMaterial === material.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.3s'
                                                }}
                                            />
                                        </button>
                                    </div>
                                </div>

                                {/* 展開内容 */}
                                {expandedMaterial === material.id && (
                                    <div className="border-t border-gray-200 p-4 space-y-4">
                                        {renderMaterialContent(material)}
                                        <button
                                            onClick={() => {
                                                recordProgress(material.id);
                                            }}
                                            className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                                                isCompleted(material.id)
                                                    ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                            }`}
                                        >
                                            {isCompleted(material.id) ? '✓ 完了済み' : '学習完了とする'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* ページング */}
                {totalPages > 1 && (
                    <div className="mt-6 flex justify-center items-center gap-2">
                        <button
                            onClick={() => {
                                window.scrollTo(0, 0);
                                fetchMaterials(currentPage - 1);
                            }}
                            disabled={currentPage === 1 || loading}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                            前へ
                        </button>
                        <span className="text-gray-600">
                            ページ {currentPage} / {totalPages}
                        </span>
                        <button
                            onClick={() => {
                                window.scrollTo(0, 0);
                                fetchMaterials(currentPage + 1);
                            }}
                            disabled={currentPage === totalPages || loading}
                            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                        >
                            次へ
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default MaterialViewer;
